import React, { useState } from 'react';

import { SecondaryButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import IconCurvedArrow from '@components/Images/Rebranding/IconCurvedArrow';
import LazyImgixImage from '@components/LazyImgixImage';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { static_privacy_path } from '@routes';
import { highlight_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Newsletter } from '@utils/backend-api/newsletters';
import { FormLocation } from '@utils/enums';
import { parameterize, transformToSnakeCase } from '@utils/formatting';
import { handleSubscriberRedirect } from '@utils/newsletterHelper';

import NewsletterActionButton from '../../pages/NewsletterIndexLanding/NewsletterCard/NewsletterActionButton';
import {
  PERSONALIZED_BRIEFING,
  SHARE,
} from '../../pages/NewsletterIndexLanding/NewsletterCard/interfaces';
import ShareModal from '../../pages/NewsletterLanding/ShareModal';

interface HeaderProps {
  isSubscribed: boolean;
  newsletter: Newsletter;
}

const Header = ({ isSubscribed, newsletter }: HeaderProps) => {
  const { initialCurrentUser, isGdprRequired, offers: offersViaParams } = useAppContext();
  const [showShareModal, setShowShareModal] = useState(false);
  const { trackEvent } = useEventTracking();
  const {
    id,
    name,
    author,
    frequency,
    description,
    cardImageUrl,
    landingPageDescription,
    afterSignUpUrl,
    isProOnly,
    offerCode,
  } = newsletter;
  const currentUserPresent = !!initialCurrentUser;
  const canSubscribeToNewsletter =
    initialCurrentUser?.access_level >= newsletter.accessLevel || !newsletter.isSubscriberOnly;
  const shareToggleModal = () => setShowShareModal((prev) => !prev);

  const handleClickTracking = (clicktarget: ClickTarget, label: string) => {
    trackEvent(click({ clickTarget: clicktarget, clickText: label }));
  };
  return (
    <div>
      <div data-testid="feed-newsletter-container-header" className="mb-64 px-24">
        <div className="m-auto max-w-container-lg">
          <div className="flex flex-wrap sm:flex-nowrap sm:gap-48 md:gap-56 lg:gap-112">
            <LazyImgixImage
              src={cardImageUrl}
              wrapperClassName="!flex items-start self-start"
              className="aspect-[16/9] w-full object-cover sm:w-[305px] md:w-[386px] lg:w-[468px]"
            />
            <div className=" mt-24 w-full sm:mt-0">
              <div className="mb-4 font-suisse-intl-mono text-overline-md text-primary-magenta">
                {frequency}
              </div>
              <h3 className="my-0 font-suisse-screen text-headline-lg md:text-headline-alt-lg">
                {name}
              </h3>
              <div className="mb-16 font-suisse-screen text-byline-sm">By {author}</div>
              <p className="m-0 mb-24 font-suisse-neue text-body-md md:text-body-lg">
                {landingPageDescription}
              </p>
              {currentUserPresent ? (
                <div className="mt-24 flex flex-wrap gap-12">
                  <NewsletterActionButton
                    isPersonalizedBriefing={name.includes(PERSONALIZED_BRIEFING)}
                    afterSignUpUrl={afterSignUpUrl}
                    canSubscribeToNewsletter={canSubscribeToNewsletter}
                    currentUserSubscribed={isSubscribed}
                    id={id}
                    initialCurrentUser={initialCurrentUser}
                    isCurrentlySubscriber={initialCurrentUser?.is_subscriber}
                    isPro={isProOnly}
                    isProSubscriber={initialCurrentUser?.is_pro_subscriber}
                    newsletterName={name}
                    offers={{
                      subscribe: offersViaParams?.subscribe || offerCode,
                      upgrade: offersViaParams?.upgrade,
                    }}
                    onClickCapture={handleClickTracking}
                    redirectToAfterSubscribe={
                      !canSubscribeToNewsletter ? highlight_path(parameterize(name)) : undefined
                    }
                    isNewsletterLanding
                  />
                  <SecondaryButton
                    as="button"
                    className="group flex !max-w-[90px] items-center gap-8 rounded-[100px] !border-grayscale-gray bg-white py-6 pl-6 pr-12 hover:!bg-primary-pink focus:bg-white enabled:hover:!bg-primary-pink"
                    onClick={() => shareToggleModal()}
                    onClickCapture={() => handleClickTracking(ClickTarget.ShareButton, SHARE)}
                  >
                    <IconCurvedArrow className="stroke-white text-grayscale-gray group-hover:stroke-primary-pink group-hover:text-grayscale-charcoal dark:group-hover:text-black" />
                    <span className="font-suisse-intl text-button-sm text-grayscale-gray group-hover:text-grayscale-charcoal dark:group-hover:text-black">
                      {SHARE}
                    </span>
                  </SecondaryButton>
                </div>
              ) : (
                <div className="my-24 w-full sm:max-w-[420px]">
                  <EmailLeadCapture
                    buttonText="Get started"
                    formClassName="mb-8 sm:col-span-2"
                    formLocation={FormLocation.NewsletterSignup}
                    gdpr={isGdprRequired}
                    gdprClassName="mb-16"
                    inputClassName="w-full"
                    leadType={transformToSnakeCase(name) + `_newsletter_lead_type`}
                    newsletterIds={[Number(id)]}
                    redirectToOnSuccess={handleSubscriberRedirect({
                      afterSignUpUrl: afterSignUpUrl,
                      canSubscribeToNewsletter: canSubscribeToNewsletter,
                      isCurrentlySubscriber: initialCurrentUser?.is_subscriber,
                      isPro: isProOnly,
                      isProSubscriber: initialCurrentUser?.is_pro_subscriber,
                      offers: {
                        subscribe: offersViaParams?.subscribe || offerCode,
                      },
                      redirectTo: !canSubscribeToNewsletter
                        ? highlight_path(parameterize(name))
                        : undefined,
                    })}
                    signInOptionClass="text-center sm:text-left"
                    toastMessageOnSuccess={
                      canSubscribeToNewsletter ? `Thank you for signing up for ${name}.` : null
                    }
                    triggerNewsletterEvent={canSubscribeToNewsletter}
                  />
                  <div className="font-suisse-screen text-overline-sm">
                    By providing your email, you agree to The Information&rsquo;s{' '}
                    <a className="text-primary-magenta" href={static_privacy_path()}>
                      Privacy Policy
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showShareModal && (
        <ShareModal
          id={id}
          name={name}
          onClose={shareToggleModal}
          author={author}
          description={description}
        />
      )}
    </div>
  );
};

export default Header;
